import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

/**
 * Self-serve password reset — step 2 of 2.
 *
 * The user lands here from a link in their reset email:
 *   /reset-password?token=<opaque single-use token>
 *
 * They type a new password (twice, for the client-side typo check) and
 * we POST to /auth/reset-password. The backend validates the token,
 * updates the password, and invalidates every existing session for
 * that user (via password_changed_at + the JWT blocklist callback).
 * On success we redirect to /login with a success notice.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Basic sanity: no token in the URL at all means the user got here
  // without following a valid email link. Don't even let them try to
  // submit — send them to the request page.
  const missingToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 12) {
      // Client-side mirror of the backend policy (12+ chars).
      // The server still enforces everything else (character classes,
      // denylist) — this is just a friendlier first pass.
      setError("Password must be at least 12 characters.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setSuccess(true);
      // Hand off to /login with a flag so it can show a small success
      // banner. Brief delay so the user sees the confirmation here too.
      setTimeout(() => navigate("/login?reset=ok"), 1800);
    } catch (err) {
      setError(errorToUserMessage(err, "Failed to reset password. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Set New Password</h1>
          <p className="text-gray-500 mt-2">Choose a strong password to secure your account</p>
        </div>

        {missingToken ? (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              No reset token in the URL. Request a fresh reset link.
            </div>
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Request reset link
              </Link>
            </div>
          </div>
        ) : success ? (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
            Password updated. Redirecting to sign-in…
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  autoFocus
                  minLength={12}
                />
                <p className="text-xs text-gray-500 mt-1">
                  12+ characters, mix of upper, lower, number, or symbol.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password
                </label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? "Updating..." : "Set New Password"}
              </button>
            </form>
            <div className="text-center mt-4">
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
